import { Color, Spacing } from '@persona-id/design-tokens';
import { AiFillIcon } from '@persona-id/design-tokens/icons';
import type { Action, Location } from 'history';
import { isEqual } from 'lodash/fp';
import { DateTime } from 'luxon';
import { ChangeEvent, FormEvent, useCallback, useEffect, useRef, useState } from 'react';
import { Controller } from 'react-hook-form';
import { FormError } from 'shared/components/FormControl';
import styled from 'styled-components';

import ContextMenu from 'dashboard/components/ContextMenu';
import { Item } from 'dashboard/components/ContextMenu/ContextMenu';
import DynamicInput from 'dashboard/components/DynamicInput';
import ModalWrapper from 'dashboard/components/ModalWrapper';
import NavBack from 'dashboard/components/NavBack';
import Pagination from 'dashboard/components/Pagination';
import ReactSelect, { OptionType } from 'dashboard/components/ReactSelect';
import {
  RoutedTabbedSectionCard,
  RoutedTabbedSectionSwitch,
} from 'dashboard/components/TabbedSectionV2';
import Tooltip from 'dashboard/components/Tooltip';
import CodeEditor from 'dashboard/components-lib/CodeEditor';
import ConfirmationModal from 'dashboard/components-lib/ConfirmationModal';
import LoadingIndicator from 'dashboard/components-lib/LoadingIndicator';
import NavConfirmPrompt from 'dashboard/components-lib/NavConfirmPrompt';
import Form from 'dashboard/layouts/FormLayout/Form';
import { Container, Header, Section } from 'dashboard/layouts/IndexViewLayoutV2';
import TableLayout, {
  TableLinkTR,
  TableNullState,
  TableTD,
  TableTH,
  TableTR,
} from 'dashboard/layouts/TableLayout';
import { DATA_SOURCE_ICONS } from 'dashboard/lib/constants/data-sources';
import { MessageType } from 'dashboard/lib/interfaces/Message';
import { AccountType } from 'dashboard/models/account-type';
import { ExternalIntegrationDataSource, Table } from 'dashboard/models/external-integration';
import {
  CallResult,
  ExternalIntegrationCall,
  ExternalIntegrationCallLoadingState,
  ExternalIntegrationCallStatus,
} from 'dashboard/models/external-integration-calls';
import {
  Importer,
  IMPORTER_STATUS_TO_LABEL,
  IMPORTER_STATUS_TO_VARIANT,
  IMPORTER_TYPES_TO_DATA_SOURCE_TYPES,
  ImporterArchiveAfterCompletion,
  ImporterArraySyncMode,
  ImporterPipelineConfig,
  ImporterStatus,
  ImporterSyncMode,
  isImporterActive,
  isTableSchemaEqual,
  MANUAL_SCHEDULE_STATE,
  QUERYABLE_IMPORTERS,
  RECURRABLE_IMPORTERS,
} from 'dashboard/models/importer';
import {
  IMPORTER_RUN_STATUS_TO_LABEL,
  IMPORTER_RUN_STATUS_TO_VARIANT,
  ImporterRun,
  ImporterRunStatus,
} from 'dashboard/models/importer-run';
import { TransactionType } from 'dashboard/models/transaction-type';
import { showMessage } from 'dashboard/redux/actions/messages';
import DashboardImporterViewSettingsTab from 'dashboard/views/DashboardImporterView/DashboardImporterViewSettingsTab';
import { EditImporterData } from 'dashboard/views/DashboardImporterView/types';
import Button from 'design-system/components/Button';
import Card from 'design-system/components/Card';
import Radio from 'design-system/components/Radio/Radio';
import Spacer from 'design-system/components/Spacer';
import { HStack } from 'design-system/components/Stack';
import Sticker from 'design-system/components/Sticker';
import { Paragraph } from 'design-system/components/Text';
import { DATE_AND_TIME_FORMAT, TIMEZONE_NAME_ABBREVIATED } from 'lib/constants/luxonFormats';
import { flattenFieldSchema } from 'lib/fields';
import useFeatureFlag from 'lib/hooks/useFeatureFlag';
import usePrevious from 'lib/hooks/usePrevious';
import useToggle from 'lib/hooks/useToggle';
import { ApiModel, PaginationParams } from 'lib/interfaces/api';
import { useFormSecurely as useForm } from 'lib/security/reactHookForm';
import { TextEquivalenceModel } from 'lib/TextEquivalenceModel';

import SmartMappingBanner from './SmartMappingBanner';

const simpleHash = (str: string): string => {
  let hash = 0;
  if (str.length === 0) return hash.toString();

  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash; // Convert to 32bit integer
  }
  return hash.toString();
};

const sanitize = (str: string) => {
  const sanitizedStr = str.replace(/[".]/g, '');
  return `${simpleHash(str)}_#${sanitizedStr}`;
};

interface DashboardImporterViewProps {
  accountTypes: AccountType[];
  activateAndRunImporter: (importer: Importer) => Promise<void>;
  activateImporter: (importer: Importer) => Promise<void>;
  archiveImporter: (importer: Importer) => Promise<void>;
  cancelImporterRun: (importerRunId: string) => Promise<void>;
  currentPage?: number;
  dataSources: ExternalIntegrationDataSource[];
  deactivateImporter: (importer: Importer) => Promise<void>;
  externalIntegrationCall: ExternalIntegrationCall | undefined;
  handlePageLinkClick: (pageNumber: number) => void;
  handlePageSizeClick: (pageSize: number) => void;
  handleQueryCancel: () => void;
  hasTransactionsEnabled: boolean;
  importer: Importer;
  importerRuns: ImporterRun[];
  pageSize?: number;
  paginationParams?: PaginationParams;
  queryLoadingState: ExternalIntegrationCallLoadingState;
  runImporter: (importer: Importer) => Promise<void>;
  runQuery: (externalIntegrationId: string, query: string) => Promise<void>;
  showMessage: typeof showMessage;
  txnTypes: TransactionType[];
  updateImporter: (values: EditImporterData) => Promise<void>;
  updateImporterName: (name: string) => Promise<void>;
}

const HeaderTitle = styled.div`
  display: flex;
  align-items: center;
`;

const StatusWrapper = styled.div`
  display: flex;
  padding-left: ${Spacing[2]};
  height: 22px;
`;

const HeaderCardIcon = styled.div`
  margin-right: ${Spacing[2]};
  width: 40px;
  height: 40px;
  background: ${Color.CorePurple100};
  display: flex;
  justify-content: center;
  align-items: center;

  svg * {
    stroke: ${Color.CorePurple500};
  }
`;

const CardBodyContainer = styled.div`
  display: flex;
`;

const HeaderCardTable = styled(TableLayout)`
  width: 100%;
`;

const HeaderCardRow = styled(TableTR)`
  padding-top: ${Spacing[1]};
  // override hover behavior so we dont change highlight row if hovered
  &:hover {
    background-color: ${({ theme }) => theme.color.background.primary};
  }
`;

const HeaderCardTH = styled(TableTH)`
  background-color: ${({ theme }) => theme.color.background.primary};
  color: ${Color.CoreSlate700};
  padding: 0px ${Spacing[1]};
`;

const HeaderCardTD = styled(TableTD)`
  flex: 2 1 auto;
  padding: 0px ${Spacing[1]};
  border: none;
`;
const HeaderCardLabel = styled.div`
  padding-top: ${Spacing[1]};
`;

const EditorContainer = styled.div`
  height: 120px;
`;

const ButtonContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
`;

const PersonaFieldTH = styled(TableTH)`
  width: 25%;
`;

const AiGenerationMessage = styled.div`
  display: inline-flex;
  align-items: center;
  color: ${({ theme }) => theme.color.text.secondary};
  font-size: ${Spacing[3]};
  margin-top: ${Spacing[2]};
  padding: ${Spacing[1]} ${Spacing[2]};
  border-radius: ${Spacing[1]};
`;

const StyledAiFillIcon = styled(AiFillIcon)`
  color: ${({ theme }) => theme.color.primary.brand800};
  margin-right: ${Spacing[2]};
`;

const DashboardImporterView = ({
  accountTypes,
  activateAndRunImporter,
  activateImporter,
  archiveImporter,
  cancelImporterRun,
  currentPage,
  dataSources,
  deactivateImporter,
  externalIntegrationCall,
  handlePageLinkClick,
  handlePageSizeClick,
  handleQueryCancel,
  hasTransactionsEnabled,
  importer,
  importerRuns,
  pageSize,
  paginationParams,
  queryLoadingState,
  runImporter,
  runQuery,
  showMessage,
  txnTypes,
  updateImporter,
  updateImporterName,
}: DashboardImporterViewProps) => {
  const compareCreatedAtDesc = (
    a: Importer | ExternalIntegrationDataSource,
    b: Importer | ExternalIntegrationDataSource,
  ) => {
    return (
      DateTime.fromISO(b.attributes['created-at']).valueOf() -
      DateTime.fromISO(a.attributes['created-at']).valueOf()
    );
  };

  const isQueryable = QUERYABLE_IMPORTERS.includes(importer.type);
  const currentlyAttachedDataSource: ExternalIntegrationDataSource | undefined = dataSources.find(
    (e) => e.id === importer.relationships['external-integration'].data.id,
  );
  const defaultTable =
    importer.attributes['pipeline-config']['table-name'] ||
    currentlyAttachedDataSource?.attributes.schema?.tables[0]?.name;
  const defaultBaseQuery = `SELECT * FROM ${defaultTable}`;
  const currentBaseQuery = importer.attributes['pipeline-config']['base-query'] || defaultBaseQuery;

  const form = useForm<EditImporterData>({
    mode: 'onChange',
    defaultValues: {
      dataSourceId: importer.relationships['external-integration'].data.id,
      destination: importer.attributes['pipeline-config']['destination'] || 'Account',
      destinationTypeId:
        importer.attributes['pipeline-config']['destination-type-id'] || accountTypes[0].id,
      tableName: defaultTable,
      syncMode: importer.attributes['pipeline-config']['sync-mode'] || ImporterSyncMode.Upsert,
      arraySyncMode:
        importer.attributes['pipeline-config']['array-sync-mode'] || ImporterArraySyncMode.Append,
      archiveAfterCompletion: importer.attributes['pipeline-config']['archive-after-completion']
        ? ImporterArchiveAfterCompletion.True
        : ImporterArchiveAfterCompletion.False,
      get sourceDestColumnMapping(): { [key: string]: string } {
        const unsanitized =
          importer.attributes['pipeline-config']['source-dest-column-mapping'] || {};
        const sanitized: { [key: string]: string } = {};
        Object.keys(unsanitized).forEach((key) => {
          sanitized[sanitize(key)] = unsanitized[key];
        });
        return sanitized;
      },
      get baseQuery(): string {
        return currentBaseQuery || defaultBaseQuery;
      },
      get updatedAtCursor(): string | undefined {
        const cursorOrder = importer.attributes['pipeline-config']['cursor-order'];
        return cursorOrder ? cursorOrder.components[0]?.['field-name'] : undefined;
      },
      get primaryIdCursor(): string | undefined {
        const cursorOrder = importer.attributes['pipeline-config']['cursor-order'];
        return cursorOrder ? cursorOrder.components[1]?.['field-name'] : undefined;
      },
      get scheduleInterval(): string | undefined {
        const schedule = importer.attributes['pipeline-config']['schedule'];
        return schedule?.interval_seconds?.toString() || MANUAL_SCHEDULE_STATE;
      },
    },
  });

  const {
    clearErrors,
    control,
    formState: { errors, isDirty, isSubmitting },
    handleSubmit,
    register,
    reset,
    setError,
    setValue,
    watch,
  } = form;

  // ---------------------------------------------------------------------------------------
  // Name
  // ---------------------------------------------------------------------------------------
  const [importerNameState, setImporterNameState] = useState(importer.attributes.name);
  const handleChangeImporterName = (e: ChangeEvent<HTMLInputElement>) => {
    setImporterNameState(e.target.value);
  };

  // triggered upon clicking away from the name input field
  const handleBlurImporterName = () => {
    if (importerNameState !== importer.attributes.name) {
      updateImporterName(importerNameState);
    }
  };
  // triggered upon pressing Enter when name input field is focused
  const handleSubmitNameChange = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    // unfocus the name input field, which should trigger the blue callback
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
  };

  // ---------------------------------------------------------------------------------------
  // DataSource
  // ---------------------------------------------------------------------------------------
  const dataSourceId = watch('dataSourceId');
  const dataSource: ExternalIntegrationDataSource | undefined = dataSources.find(
    (e) => e.id === dataSourceId,
  );
  // if dataSource does not exist, it could mean dataSource is archived
  const dataSourceValid = dataSource != null;
  const canEditDataSource =
    importer.attributes.status === ImporterStatus.Inactive && dataSourceValid;

  const dataSourceOptions: OptionType[] = canEditDataSource
    ? dataSources
        .sort(compareCreatedAtDesc)
        .filter((dataSource) =>
          IMPORTER_TYPES_TO_DATA_SOURCE_TYPES[importer.type].includes(dataSource.type),
        )
        .map((dataSource) => {
          return { label: dataSource.attributes.name, value: dataSource.id };
        })
    : [];

  const DataSourceIcon = dataSource
    ? DATA_SOURCE_ICONS[dataSource.type as keyof typeof DATA_SOURCE_ICONS]
    : // arbitrarily default to csv icon if dataSource not found
      DATA_SOURCE_ICONS[ApiModel.ExternalIntegrationDataSourceCsv];

  // ---------------------------------------------------------------------------------------
  // Table Schema
  // ---------------------------------------------------------------------------------------
  function findRowValues(
    callResult: CallResult,
  ): Record<string, string | number | boolean | null | undefined> {
    const result: Record<string, string | number | boolean | null | undefined> = {};
    callResult.keys.forEach((key) => {
      let valueFound = false;
      for (const row of callResult.rows) {
        if (row[key]) {
          result[key] = row[key];
          valueFound = true;
          break;
        }
      }
      if (!valueFound) {
        result[key] = null;
      }
    });
    return result;
  }

  const makeTableSchemaFromCallResult = (callResult: CallResult): Table => {
    const previewRowValues = findRowValues(callResult);
    const schema: Table = {
      name: 'result',
      columns: Object.keys(previewRowValues).map((key: string) => {
        return {
          name: key,
          type: 'string',
          preview: previewRowValues[key],
        };
      }),
    };

    return schema;
  };

  const tableName = watch('tableName');
  const schema = isQueryable
    ? ExternalIntegrationCallStatus.Completed === queryLoadingState.status &&
      externalIntegrationCall &&
      externalIntegrationCall?.attributes['call-result']
      ? makeTableSchemaFromCallResult(externalIntegrationCall?.attributes['call-result'])
      : dataSource?.attributes.schema?.tables.find((t) => t.name === defaultTable)
    : dataSource?.attributes.schema?.tables.find((t) => t.name === tableName);
  const displaySchemaColumns =
    !schema && importer.attributes.status === ImporterStatus.Archived
      ? Object.keys(importer.attributes['pipeline-config']['source-dest-column-mapping'] ?? {}).map(
          (val) => {
            return {
              name: val,
              type: 'text',
              preview: null,
            };
          },
        )
      : schema?.columns;

  const unsanitizedColumnNameMap: { [key: string]: string } = {};

  // if the schema field has '"' in it, it will break the form. So we need to sanitize it
  if (schema) {
    schema.columns.map((column) => {
      const sanitizedColumnName = sanitize(column.name);
      // map the sanitized column name to the original column name
      unsanitizedColumnNameMap[sanitizedColumnName] = column.name;
    });
  }

  // ---------------------------------------------------------------------------------------
  // External Integration Calls, Base Query, and TableName
  // ---------------------------------------------------------------------------------------
  const baseQuery = watch('baseQuery');
  const canEditBaseQuery = canEditDataSource;

  const sourceDestColumnMappings = watch('sourceDestColumnMapping');
  const resetSourceDestColumnMappings = useCallback(() => {
    const clearedSourceDestColumnMappings: { [key: string]: string } = {};
    for (const key of Object.keys(sourceDestColumnMappings)) {
      clearedSourceDestColumnMappings[key] = '';
    }
    setValue('sourceDestColumnMapping', clearedSourceDestColumnMappings, { shouldDirty: true });
  }, [sourceDestColumnMappings]);

  const [hasRunBaseQuery, setHasRunBaseQuery] = useState<boolean>(false);
  useEffect(() => {
    if (queryLoadingState.status === ExternalIntegrationCallStatus.Completed) {
      setHasRunBaseQuery(true);
      if (baseQuery !== currentBaseQuery) {
        resetSourceDestColumnMappings();
      }
    }
  }, [queryLoadingState.status]);
  useEffect(() => {
    if (baseQuery === currentBaseQuery) {
      setHasRunBaseQuery(true);
    }
  }, [baseQuery]);

  const makeExternalIntegrationCall = useCallback(
    async (baseQuery: string) => {
      // todo: [gary.johnson] validate baseQuery before making call
      if (!baseQuery) {
        return;
      }
      setAiGenerationState((prevState) => ({ ...prevState, userInteracted: true }));
      await runQuery(dataSourceId, baseQuery);
    },
    [dataSourceId, baseQuery],
  );
  const queryInProgress = queryLoadingState.status === ExternalIntegrationCallStatus.Processing;

  const canEditTableName = canEditDataSource;
  // Can't really think of a better way to trigger a reset upon any table change but not the first
  const isTableNameInitialLoad = useRef<boolean>(true);
  useEffect(() => {
    if (isTableNameInitialLoad.current) {
      isTableNameInitialLoad.current = false;
    } else {
      resetSourceDestColumnMappings();
    }
  }, [tableName]);
  const isDataSourceInitialLoad = useRef<boolean>(true);

  const prevDataSourceId = usePrevious(dataSourceId);
  useEffect(() => {
    if (isDataSourceInitialLoad.current) {
      isDataSourceInitialLoad.current = false;
    } else {
      const newTableName = dataSource?.attributes?.schema?.tables?.[0]?.name || '';
      setValue('tableName', newTableName);
      if (isQueryable) {
        setHasRunBaseQuery(false);
        const newBaseQuery = `SELECT * FROM ${newTableName}`;
        setValue('baseQuery', newBaseQuery, { shouldDirty: true });

        // auto run query when switching data sources
        if (queryInProgress) {
          handleQueryCancel();
        }
        makeExternalIntegrationCall(newBaseQuery);
      }
      // redundant resetSourceDestColumnMappings in case tableName is the same when switching between datasources
      // or if the importer does uses baseQuery rather than tableName

      const oldDataSource: ExternalIntegrationDataSource | undefined = dataSources.find(
        (e) => e.id === prevDataSourceId,
      );

      if (!isTableSchemaEqual(oldDataSource, dataSource)) {
        resetSourceDestColumnMappings();
      }
    }
  }, [dataSourceId]);

  // ---------------------------------------------------------------------------------------
  // Destination & Destination Type
  // ---------------------------------------------------------------------------------------
  const destination = watch('destination');
  const destinationTypeId = watch('destinationTypeId');
  const canEditDestination = canEditDataSource;

  const destinationOptions: OptionType<NonNullable<ImporterPipelineConfig['destination']>>[] = [
    { label: 'Account', value: 'Account' },
    {
      label: 'Transaction',
      value: 'Txn',
      disabled: !hasTransactionsEnabled || txnTypes.length === 0,
    },
  ];

  let destinationTypes: (AccountType | TransactionType)[] = [];
  if (destination === 'Account') {
    destinationTypes = accountTypes;
  } else if (destination === 'Txn') {
    destinationTypes = txnTypes;
  }
  const destinationType = destinationTypes.find((d) => d.id === destinationTypeId);
  useEffect(() => {
    if (!destinationType) {
      setValue('destinationTypeId', destinationTypes[0]?.id);
    }
  }, [destinationType]);
  const destinationTypeIdOptions = destinationTypes.map((d) => {
    return { label: d.attributes['name'], value: d.id };
  });

  // ---------------------------------------------------------------------------------------
  // Source Dest Column Mappings
  // ---------------------------------------------------------------------------------------
  const canEditSourceDestColumnMappings = canEditDataSource;

  const arraySyncMode = watch('arraySyncMode');
  const enableJsonParsingForArrays =
    useFeatureFlag('dashboard:importers-enable-csv-arrays-using-json') &&
    arraySyncMode === ImporterArraySyncMode.Replace;
  const flattenedFieldSchema = flattenFieldSchema(
    destinationType?.attributes['field-schemas'] ?? [],
    true,
    enableJsonParsingForArrays,
  );

  const destinationColumnOptions: OptionType[] = flattenedFieldSchema
    .filter((fs) => fs.active)
    .map((fs) => {
      return { label: fs.value, value: fs.value };
    });

  const inactiveColumnOptions: string[] = flattenedFieldSchema
    .filter((fs) => !fs.active)
    .map((fs) => fs.value);

  const destinationTypeMapping: { [key: string]: string } = flattenedFieldSchema.reduce(
    (acc, fs) => {
      return { ...acc, [fs.value]: fs.type };
    },
    {},
  );

  destinationColumnOptions.unshift(
    { label: 'reference_id', value: 'reference_id' },
    { label: 'tag', value: 'tag' },
    { label: 'status', value: 'status' },
  );
  destinationTypeMapping['reference_id'] = 'string';
  destinationTypeMapping['tag'] = 'string';
  destinationTypeMapping['status'] = 'string';

  const fieldNames: `sourceDestColumnMapping.${string}`[] =
    schema?.columns.map(
      (column: { name: string }) =>
        `sourceDestColumnMapping.${sanitize(column.name)}` as `sourceDestColumnMapping.${string}`,
    ) || [];
  const takenOptions = watch(fieldNames);

  // ---------------------------------------------------------------------------------------
  // Settings Tab
  // ---------------------------------------------------------------------------------------
  const recurringImportersEnabled = useFeatureFlag('dashboard:graph-recurring-importers');
  const isRecurrable = recurringImportersEnabled && RECURRABLE_IMPORTERS.includes(importer.type);

  // If importer is activated, only allow editing cursors if no cursor has been set yet.
  const canEditCursors =
    importer.attributes.status === ImporterStatus.Active
      ? importer.attributes['pipeline-config']['cursor-order'] == null && dataSourceValid
      : importer.attributes.status === ImporterStatus.Inactive && dataSourceValid;
  const canEditSchedule = importer.attributes.status !== ImporterStatus.Archived;

  // ---------------------------------------------------------------------------------------
  // Status Buttons
  // ---------------------------------------------------------------------------------------

  // this also means that the data source is csv (could be isCSVDatasource(dataSource) instead)
  const isOneTimeRun =
    importer.relationships['external-integration'].data.type ===
    'external-integration/data-source/csv';
  const hasRunInProgress = importerRuns.some(
    (run: ImporterRun) => run.attributes.status === ImporterRunStatus.Running,
  );
  const canRun =
    isImporterActive(importer) &&
    (importerRuns.length === 0 || !isOneTimeRun) &&
    dataSourceValid &&
    !hasRunInProgress;
  const canSave = importer.attributes.status !== ImporterStatus.Archived;
  const canActivate = importer.attributes.status === ImporterStatus.Inactive && dataSourceValid;
  const canDeactivate =
    isImporterActive(importer) &&
    !importerRuns.find((ir) => ir.attributes.status === ImporterRunStatus.Running) &&
    (importerRuns.length === 0 || !isOneTimeRun) &&
    dataSourceValid;
  // verify not already archived and no importer run is ongoing
  const canArchive =
    importer.attributes.status !== ImporterStatus.Archived &&
    !importerRuns.find((ir) => ir.attributes.status === ImporterRunStatus.Running);

  // ---------------------------------------------------------------------------------------
  // On Submit
  // ---------------------------------------------------------------------------------------
  const importerValidationError = (enableJsonParsingForArrays: boolean) => {
    const savedSourceDestColumnMappings =
      importer.attributes['pipeline-config']['source-dest-column-mapping'] || {};
    if (!Object.values(savedSourceDestColumnMappings).includes('reference_id')) {
      setError('sourceDestColumnMapping', {
        type: 'custom',
        message: 'Please map a source column to the Persona reference_id field.',
      });
      return 'Invalid Source-Destination Column Mappings';
    }
    // For array of hashes there are two possible ways of mapping to it. You can either map to the entire array (custom.array_of_hashes[]) or you can choose to
    // map to a specific key within the hash (custom.array_of_hashes[].key). We want to prevent the user
    // from doing both at the same time.
    if (enableJsonParsingForArrays) {
      Object.values(savedSourceDestColumnMappings).forEach((column) => {
        if (column.endsWith('[]')) {
          const sameColumnNameCount = Object.values(savedSourceDestColumnMappings).filter((c) =>
            c.startsWith(column),
          ).length;
          if (sameColumnNameCount > 1) {
            setError('sourceDestColumnMapping', {
              type: 'custom',
              message: `Please map to source column ${column} only once.`,
            });
            return 'Invalid Source-Destination Column Mappings';
          }
        }
      });
    }
    return null;
  };

  const onSubmit = handleSubmit(async (values) => {
    if (canEditSourceDestColumnMappings) {
      // Sanitize out empty string mappings. Empty string mappings occurs when clicking on the 'x' on the ReactSelect
      Object.keys(values.sourceDestColumnMapping || {}).forEach((k) => {
        if (values.sourceDestColumnMapping[k] === '') {
          delete values.sourceDestColumnMapping[k];
        }
      });
    }

    // get original values using sanitized values
    const originalSourceDestColumnMapping: { [key: string]: string } = {};
    Object.keys(values.sourceDestColumnMapping).forEach((k) => {
      originalSourceDestColumnMapping[unsanitizedColumnNameMap[k]] =
        values.sourceDestColumnMapping[k];
    });

    const originalValues: EditImporterData = {
      ...values,
      sourceDestColumnMapping: originalSourceDestColumnMapping,
    };

    // TODO clear out invalid entries, where the value doesn't exist in the currently-selected destinationType's schema (destinationColumnOptions)
    await updateImporter(originalValues);

    // The isDirty attribute in react-hook-form simply checks for equality (===) of the current form values with the passed default values.
    // After saving, we want to reset the default values to be exactly that of the current formState
    // values is a sanitized copy of the formState values, so we overwrite the 'sourceDestColumnMapping' field so that the
    // equality passes (I <3 JS equality checking)
    values['sourceDestColumnMapping'] = form.getValues('sourceDestColumnMapping');
    reset(values);
  });

  const [
    cancelRunConfirmationModalOpen,
    { enable: showConfirmationModal, disable: hideCancelConfirmationModal },
  ] = useToggle(false);

  const [selectedImporterRunId, setSelectedImporterRunId] = useState<string | undefined>(undefined);

  const openCancelConfirmationModal = (importerRunId: string) => {
    setSelectedImporterRunId(importerRunId);
    showConfirmationModal();
  };

  const closeCancelConfirmationModal = () => {
    setSelectedImporterRunId(undefined);
    hideCancelConfirmationModal();
  };

  const handleCancelImporterRun = () => {
    if (selectedImporterRunId) {
      cancelImporterRun(selectedImporterRunId);
      setSelectedImporterRunId(undefined);
    }
    hideCancelConfirmationModal();
  };

  // ---------------------------------------------------------------------------------------
  // Smart Mappings
  // ---------------------------------------------------------------------------------------

  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const [aiGenerationState, setAiGenerationState] = useState({
    completed: false,
    userInteracted: false,
  });

  const [aiSuggestions, setAiSuggestions] = useState<{ [key: string]: boolean }>({});

  useEffect(() => {
    if (isDirty) {
      setAiGenerationState((prevState) => ({ ...prevState, userInteracted: true }));
    }
  }, [isDirty]);

  const handleAutoMapping = () => {
    setIsLoading(true);
    setIsError(false);

    setTimeout(() => {
      callAutoMapping();
      setIsLoading(false);
    }, 2000);
  };

  const callAutoMapping = useCallback(() => {
    try {
      const sourceColumns = schema?.columns.map((column) => column.name) || [];
      const personaFields = destinationColumnOptions.map((option) => option.value as string);

      const mappings = TextEquivalenceModel.autoMapFields(sourceColumns, personaFields);

      const newAiSuggestions: { [key: string]: boolean } = {};

      Object.entries(mappings).forEach(([sourceColumn, personaField]) => {
        const sanitizedKey = sanitize(sourceColumn);
        if (personaField) {
          setValue(`sourceDestColumnMapping.${sanitizedKey}`, personaField, {
            shouldDirty: true,
          });
          newAiSuggestions[sanitizedKey] = true;
        } else {
          setValue(`sourceDestColumnMapping.${sanitizedKey}`, '', {
            shouldDirty: true,
          });
          newAiSuggestions[sanitizedKey] = false;
        }
      });

      setAiSuggestions(newAiSuggestions);
      setAiGenerationState({ completed: true, userInteracted: false });
    } catch (error) {
      setIsError(true);
    }
  }, [schema?.columns, destinationColumnOptions, setValue]);

  return (
    <>
      <ModalWrapper
        isOpen={cancelRunConfirmationModalOpen}
        handleCloseModal={closeCancelConfirmationModal}
      >
        <ConfirmationModal
          cancelAction={closeCancelConfirmationModal}
          confirmAction={handleCancelImporterRun}
          header='Cancel Run?'
          confirmActionText='Yes'
          cancelActionText='No'
        >
          This action is permanent and can’t be undone.
        </ConfirmationModal>
      </ModalWrapper>
      <NavConfirmPrompt
        prevent={(currentLocation: Location, nextLocation?: Location, _action?: Action) => {
          // we allow switching between tabs. that is switching between locations
          // /dashboard/imports/importers/<token>/data-mapping and
          // /dashboard/imports/importers/<token>/schedule, etc.
          const currentBasePath = currentLocation.pathname.split('/').slice(0, -1);
          const nextBasePath = nextLocation?.pathname?.split('/')?.slice(0, -1);
          if (isEqual(currentBasePath, nextBasePath)) {
            return false;
          }
          return isDirty;
        }}
        message='Are you sure you want to leave?'
      >
        The changes you made wont be saved.
      </NavConfirmPrompt>
      <Header
        title=''
        prepend={
          <HeaderTitle>
            <NavBack to='/dashboard/imports/importers' />
            <Form onSubmit={handleSubmitNameChange}>
              <DynamicInput
                aria-label="Edit importer's name"
                value={importerNameState}
                onBlur={handleBlurImporterName}
                onChange={handleChangeImporterName}
              />
            </Form>
            <StatusWrapper>
              <Sticker variant={IMPORTER_STATUS_TO_VARIANT[importer.attributes.status]}>
                {IMPORTER_STATUS_TO_LABEL[importer.attributes.status]}
              </Sticker>
            </StatusWrapper>
          </HeaderTitle>
        }
      >
        <HStack>
          {canActivate && isOneTimeRun && (
            <Button
              disabled={isDirty || isSubmitting}
              width='auto'
              onClick={() => {
                const validationError = importerValidationError(enableJsonParsingForArrays);
                if (validationError) {
                  showMessage(`Cannot activate Importer: ${validationError}`, MessageType.Failure);
                } else {
                  activateAndRunImporter(importer);
                }
              }}
            >
              Activate & Run
            </Button>
          )}
          {canDeactivate && (
            <Button
              disabled={isDirty || isSubmitting}
              width='auto'
              onClick={() => {
                deactivateImporter(importer);
              }}
            >
              Deactivate
            </Button>
          )}
          {canArchive && (
            <Button
              disabled={isDirty || isSubmitting}
              width='auto'
              onClick={() => {
                archiveImporter(importer);
              }}
            >
              Archive
            </Button>
          )}
          {canActivate && !isOneTimeRun && (
            <Button
              disabled={isDirty || isSubmitting}
              width='auto'
              onClick={() => {
                const validationError = importerValidationError(enableJsonParsingForArrays);
                if (validationError) {
                  showMessage(`Cannot activate Importer: ${validationError}`, MessageType.Failure);
                } else {
                  activateImporter(importer);
                }
              }}
            >
              Activate
            </Button>
          )}
          {canRun && (
            <Button
              disabled={isDirty || isSubmitting}
              width='auto'
              onClick={() => {
                runImporter(importer);
              }}
            >
              Run
            </Button>
          )}
          {canSave && (
            <Button
              disabled={
                // dont allow saving if base query changed but has not run
                (!hasRunBaseQuery && baseQuery !== currentBaseQuery) || (!isDirty && !isSubmitting)
              }
              width='auto'
              onClick={onSubmit}
            >
              Save
            </Button>
          )}
        </HStack>
      </Header>
      <Container>
        <Section>
          <Card>
            <Card.Body>
              <CardBodyContainer>
                <HeaderCardIcon>
                  <DataSourceIcon />
                </HeaderCardIcon>
                <HeaderCardTable
                  showHeader={true}
                  isEmpty={false}
                  nullState={null}
                  headerTR={
                    <>
                      <HeaderCardTH>Data Source</HeaderCardTH>
                      <HeaderCardTH>Destination</HeaderCardTH>
                      <HeaderCardTH>Destination Type</HeaderCardTH>
                      <HeaderCardTH>Created attributes</HeaderCardTH>
                      <HeaderCardTH>Importer ID</HeaderCardTH>
                    </>
                  }
                >
                  <HeaderCardRow key='importer-header-card-row'>
                    <HeaderCardTD>
                      {canEditDataSource ? (
                        // TODO: (bjon) I think if you switch data sources, the data mapping fields do not get reset/changed
                        // which may be annoying to the user. I think fixing this will mean caching the source dest column mappings
                        // by data source id. Will defer for now.
                        <Controller
                          control={control}
                          defaultValue={dataSourceId}
                          name='dataSourceId'
                          render={({ field: { onChange, value } }) => {
                            return (
                              <ReactSelect
                                id='dashboard-importer-view__data-source'
                                aria-label='Data source'
                                isClearable={false}
                                value={value}
                                onChange={(value) => {
                                  onChange(value);
                                }}
                                options={dataSourceOptions}
                                placeholder='Data source'
                                portal={true}
                              />
                            );
                          }}
                        />
                      ) : dataSource ? (
                        <HeaderCardLabel>{dataSource.attributes.name}</HeaderCardLabel>
                      ) : (
                        <HeaderCardLabel>Redacted Data source</HeaderCardLabel>
                      )}
                    </HeaderCardTD>
                    <HeaderCardTD>
                      <div>
                        {canEditDestination ? (
                          <Controller
                            control={control}
                            defaultValue={destination}
                            name='destination'
                            render={({ field: { onChange, value } }) => {
                              return (
                                <ReactSelect
                                  id='dashboard-importer-view__destination'
                                  aria-label='Destination'
                                  isClearable={false}
                                  value={value}
                                  onChange={(value) => {
                                    onChange(value);
                                    resetSourceDestColumnMappings();
                                  }}
                                  options={destinationOptions}
                                  placeholder='Destination'
                                  portal={true}
                                />
                              );
                            }}
                          />
                        ) : (
                          <HeaderCardLabel>
                            {importer.attributes['pipeline-config']['destination'] || 'Account'}
                          </HeaderCardLabel>
                        )}
                      </div>
                    </HeaderCardTD>
                    <HeaderCardTD>
                      {' '}
                      <div>
                        {canEditDestination ? (
                          <Controller
                            control={control}
                            defaultValue={destinationTypeId}
                            name='destinationTypeId'
                            render={({ field: { onChange, value } }) => {
                              return (
                                <ReactSelect
                                  id='dashboard-importer-view__destination-type-id'
                                  aria-label='Destination Type'
                                  isClearable={false}
                                  value={value}
                                  onChange={(value) => {
                                    onChange(value);
                                    resetSourceDestColumnMappings();
                                  }}
                                  options={destinationTypeIdOptions}
                                  placeholder='Destination Type'
                                  portal={true}
                                />
                              );
                            }}
                          />
                        ) : (
                          <HeaderCardLabel>{destinationType?.attributes['name']}</HeaderCardLabel>
                        )}
                      </div>
                    </HeaderCardTD>
                    <HeaderCardTD>
                      <HeaderCardLabel>
                        {DateTime.fromISO(importer.attributes['created-at']).toFormat(
                          DATE_AND_TIME_FORMAT,
                        )}
                      </HeaderCardLabel>
                    </HeaderCardTD>
                    <HeaderCardTD>
                      <HeaderCardLabel>{importer.id}</HeaderCardLabel>
                    </HeaderCardTD>
                  </HeaderCardRow>
                </HeaderCardTable>
              </CardBodyContainer>
            </Card.Body>
          </Card>

          <RoutedTabbedSectionCard
            tabs={[
              {
                name: 'Activity',
                path: 'activity',
              },
              {
                name: 'Data Mapping',
                path: 'data-mapping',
              },
              {
                name: 'Settings',
                path: 'settings',
              },
            ]}
          />

          <RoutedTabbedSectionSwitch
            tabs={[
              {
                path: 'activity',
                contents: (
                  <Card>
                    <Card.Body>
                      <Card.Title>Recent Activity</Card.Title>
                      <TableLayout
                        showHeader={true}
                        isEmpty={importerRuns.length === 0}
                        nullState={
                          <TableNullState>
                            <Paragraph>
                              Manually trigger or schedule an import run to show up in this list.
                            </Paragraph>
                          </TableNullState>
                        }
                        headerTR={
                          <>
                            <TableTH>ID</TableTH>
                            <TableTH>
                              Started at ({DateTime.local().toFormat(TIMEZONE_NAME_ABBREVIATED)})
                            </TableTH>
                            <TableTH>
                              Completed at ({DateTime.local().toFormat(TIMEZONE_NAME_ABBREVIATED)})
                            </TableTH>
                            <TableTH>Data summary</TableTH>
                            <TableTH>Status</TableTH>
                            <TableTH></TableTH>
                          </>
                        }
                      >
                        {importer.attributes['next-run-at'] && currentPage === 1 && (
                          <TableTR key='importer_next_run'>
                            <TableTD>--</TableTD>
                            <TableTD>
                              {DateTime.fromISO(importer.attributes['next-run-at']).toFormat(
                                DATE_AND_TIME_FORMAT,
                              )}
                            </TableTD>
                            <TableTD>--</TableTD>
                            <TableTD>No information available.</TableTD>
                            <TableTD>
                              <Sticker variant='grey'>Up Next</Sticker>
                            </TableTD>
                            <TableTD></TableTD>
                          </TableTR>
                        )}
                        {
                          // TODO: (bjon) BE should sort by created_at. RN cancelled runs go straight to the bottom
                          importerRuns.map((i) => {
                            const canCancel = [
                              ImporterRunStatus.Pending,
                              ImporterRunStatus.Running,
                            ].includes(i.attributes.status);
                            const items: Item[] = canCancel
                              ? [
                                  {
                                    id: 'cancel',
                                    onClick: () => openCancelConfirmationModal(i.id),
                                    label: 'Cancel',
                                    variant: 'danger',
                                  } as Item,
                                ].filter(Boolean)
                              : [];

                            return (
                              <TableLinkTR
                                key={i.id}
                                to={`/dashboard/imports/importers/${importer.id}/${i.id}/run-details`}
                              >
                                <TableTD>{i.id}</TableTD>
                                <TableTD>
                                  {DateTime.fromISO(i.attributes['started-at']).toFormat(
                                    DATE_AND_TIME_FORMAT,
                                  )}
                                </TableTD>
                                <TableTD>
                                  {i.attributes['ended-at']
                                    ? DateTime.fromISO(i.attributes['ended-at']).toFormat(
                                        DATE_AND_TIME_FORMAT,
                                      )
                                    : ''}
                                </TableTD>

                                <TableTD>
                                  {/*Todo: [gary.johnson] once ErrorDetails is reintroduced, that data needs to be represented here*/}
                                  No information available.
                                </TableTD>
                                <TableTD>
                                  <Sticker
                                    variant={IMPORTER_RUN_STATUS_TO_VARIANT[i.attributes.status]}
                                  >
                                    {IMPORTER_RUN_STATUS_TO_LABEL[i.attributes.status]}
                                  </Sticker>
                                </TableTD>
                                <TableTD>
                                  <ContextMenu
                                    items={items}
                                    renderItem={(item) => (
                                      <ContextMenu.Item
                                        id={`importer-runs-table-row-context-menu-item_${importer.id}`}
                                        key={item.id}
                                        onClick={(e) => {
                                          // Needed to avoid triggering row click below
                                          e.stopPropagation();
                                          e.preventDefault();
                                          item.onClick?.();
                                        }}
                                        variant={item.variant}
                                      >
                                        {item.label}
                                      </ContextMenu.Item>
                                    )}
                                  />
                                </TableTD>
                              </TableLinkTR>
                            );
                          })
                        }
                      </TableLayout>
                      <Pagination
                        pageSize={pageSize}
                        paginationParams={paginationParams}
                        handlePageSizeClick={handlePageSizeClick}
                        handlePageLinkClick={handlePageLinkClick}
                      />
                    </Card.Body>
                  </Card>
                ),
              },
              {
                path: 'data-mapping',
                contents: (
                  <>
                    <Card>
                      <Card.Body>
                        <Card.Title>{`Importer Table${
                          dataSource?.attributes.schema?.tables &&
                          dataSource?.attributes.schema?.tables?.length > 1
                            ? 's'
                            : ''
                        }`}</Card.Title>
                        <TableLayout
                          showHeader={true}
                          isEmpty={
                            !dataSource || dataSource?.attributes.schema?.tables.length === 0
                          }
                          nullState={
                            <TableNullState>
                              <Paragraph>Invalid data source. No table found.</Paragraph>
                            </TableNullState>
                          }
                          headerTR={
                            <>
                              <TableTH>Name</TableTH>
                              <TableTH>Columns</TableTH>
                              {!isQueryable && <TableTH></TableTH>}
                            </>
                          }
                        >
                          {dataSource?.attributes.schema?.tables.map(
                            (table: { name: string; columns: { name: string }[] }) => {
                              return (
                                <tr key={table.name}>
                                  <TableTD>{table.name}</TableTD>
                                  <TableTD>
                                    {table.columns
                                      .map((column: { name: string }) => column.name)
                                      .join(', ')}
                                  </TableTD>
                                  {!isQueryable && (
                                    <TableTD>
                                      <Radio
                                        label={table.name}
                                        {...register('tableName', { required: true })}
                                        disabled={!canEditTableName}
                                        value={table.name}
                                      />
                                    </TableTD>
                                  )}
                                </tr>
                              );
                            },
                          )}
                        </TableLayout>
                      </Card.Body>
                    </Card>
                    <Card>
                      <Card.Body gap={0}>
                        <Card.Title>Data mapping</Card.Title>
                        {isQueryable && (
                          <>
                            <Card.SectionHeader
                              style={{ borderBottom: 'none' }}
                              {...{
                                actions: canEditBaseQuery ? (
                                  <>
                                    <ButtonContainer>
                                      <Button
                                        width='auto'
                                        onClick={() => {
                                          if (queryInProgress) {
                                            handleQueryCancel();
                                          } else {
                                            makeExternalIntegrationCall(baseQuery);
                                          }
                                        }}
                                        variant='basic'
                                        size='sm'
                                      >
                                        {queryInProgress ? 'Cancel' : 'Preview'}
                                      </Button>
                                    </ButtonContainer>
                                  </>
                                ) : null,
                              }}
                            >
                              Query
                            </Card.SectionHeader>
                            Use the query below to modify and preview your data in the table below.
                            <Spacer height={1} />
                            <EditorContainer>
                              <CodeEditor
                                code={baseQuery}
                                onCommandEnter={() => {
                                  makeExternalIntegrationCall(baseQuery);
                                }}
                                language='sql'
                                onChange={(value) => {
                                  setHasRunBaseQuery(false);
                                  setValue('baseQuery', value, { shouldDirty: true });
                                }}
                                readOnly={!canEditBaseQuery}
                                schemas={{
                                  sql: {
                                    tables: dataSource?.attributes.schema?.tables || [],
                                  },
                                }}
                              />
                            </EditorContainer>
                            {externalIntegrationCall?.attributes?.['error-message'] && (
                              <FormError>
                                {externalIntegrationCall.attributes['error-message']}
                              </FormError>
                            )}
                            <Spacer height={2} />
                          </>
                        )}
                        <Card.SectionHeader>
                          Field mapping
                          {/*create a run button */}
                        </Card.SectionHeader>
                        {errors.sourceDestColumnMapping && (
                          <Card.Subtitle>
                            <FormError>{errors.sourceDestColumnMapping.message}</FormError>
                          </Card.Subtitle>
                        )}
                        {importer.attributes.status === ImporterStatus.Inactive && (
                          <SmartMappingBanner
                            message='Suggest smart mappings between the source and Persona field.'
                            loadingMessage='Suggesting smart mappings...'
                            errorMessage='Unable to provide suggestions. Please try again.'
                            onUseClick={handleAutoMapping}
                            isLoading={isLoading}
                            isError={isError}
                          />
                        )}
                        <TableLayout
                          showHeader={true}
                          isEmpty={queryInProgress || schema?.columns.length === 0}
                          nullState={
                            <TableNullState>
                              {queryInProgress ? (
                                <LoadingIndicator />
                              ) : (
                                <Paragraph>Select a table above.</Paragraph>
                              )}
                            </TableNullState>
                          }
                          headerTR={
                            <>
                              <TableTH>Source column</TableTH>
                              <TableTH>Data preview</TableTH>
                              <TableTH>Accepted type</TableTH>
                              <PersonaFieldTH>Persona field</PersonaFieldTH>
                            </>
                          }
                        >
                          {displaySchemaColumns &&
                            displaySchemaColumns.map(
                              (column: {
                                name: string;
                                type: string;
                                preview?: string | number | boolean | null | undefined;
                              }) => {
                                // show some indication if txn field is inactive
                                const dest = importer.attributes['pipeline-config'][
                                  'source-dest-column-mapping'
                                ]?.[column.name] as string;
                                const isInactiveField = inactiveColumnOptions.includes(dest);
                                return (
                                  <tr key={column.name}>
                                    <TableTD>{column.name}</TableTD>
                                    <TableTD>
                                      {column.preview == null ? (
                                        <Tooltip
                                          trigger='hover'
                                          tooltip={
                                            <Paragraph color='primary' size='md'>
                                              Run query for preview
                                            </Paragraph>
                                          }
                                          placement='bottom'
                                        >
                                          --
                                        </Tooltip>
                                      ) : typeof column.preview === 'object' ? (
                                        JSON.stringify(column.preview)
                                      ) : (
                                        column.preview
                                      )}
                                    </TableTD>

                                    {canEditSourceDestColumnMappings ? (
                                      <Controller
                                        control={control}
                                        defaultValue={
                                          importer.attributes['pipeline-config'][
                                            'source-dest-column-mapping'
                                          ]?.[column.name]
                                        }
                                        name={`sourceDestColumnMapping.${sanitize(column.name)}`}
                                        render={({ field: { onChange, value } }) => {
                                          const showNoSuggestions =
                                            aiSuggestions[sanitize(column.name)] === false;
                                          return (
                                            <>
                                              <TableTD>
                                                {destinationTypeMapping[value] ? (
                                                  destinationTypeMapping[value]
                                                ) : (
                                                  <Tooltip
                                                    trigger='hover'
                                                    tooltip={
                                                      <Paragraph color='primary' size='md'>
                                                        Select a Persona field to view the accepted
                                                        format
                                                      </Paragraph>
                                                    }
                                                    placement='bottom'
                                                  >
                                                    --
                                                  </Tooltip>
                                                )}
                                              </TableTD>
                                              <TableTD>
                                                <ReactSelect
                                                  id={`dashboard-importer-view__destination-${column.name}`}
                                                  aria-label={`Destination for ${column.name}`}
                                                  isClearable={true}
                                                  value={value}
                                                  // Exclude options already in-use by other source columns
                                                  options={destinationColumnOptions.filter(
                                                    (o) =>
                                                      o.value === value ||
                                                      o.value == null ||
                                                      !takenOptions.includes(o.value),
                                                  )}
                                                  onChange={(value) => {
                                                    clearErrors('sourceDestColumnMapping');
                                                    onChange(value);
                                                    setAiGenerationState((prevState) => ({
                                                      ...prevState,
                                                      userInteracted: true,
                                                    }));
                                                  }}
                                                  placeholder={
                                                    showNoSuggestions &&
                                                    !aiGenerationState.userInteracted
                                                      ? 'No suggestion found'
                                                      : 'Destination'
                                                  }
                                                  portal={true}
                                                />
                                              </TableTD>
                                            </>
                                          );
                                        }}
                                      />
                                    ) : (
                                      <>
                                        <TableTD>
                                          {destinationTypeMapping[dest]
                                            ? destinationTypeMapping[dest]
                                            : '--'}
                                        </TableTD>
                                        <TableTD>
                                          {`${dest || ''}${isInactiveField ? ' (INACTIVE)' : ''}`}
                                        </TableTD>
                                      </>
                                    )}
                                  </tr>
                                );
                              },
                            )}
                        </TableLayout>
                        {aiGenerationState.completed &&
                          !aiGenerationState.userInteracted &&
                          !isLoading && (
                            <AiGenerationMessage>
                              <StyledAiFillIcon />
                              Generated by AI
                            </AiGenerationMessage>
                          )}
                      </Card.Body>
                    </Card>
                  </>
                ),
              },
              {
                path: 'settings',
                contents: (
                  <DashboardImporterViewSettingsTab
                    form={form}
                    schema={schema as Table}
                    canEditSchedule={canEditSchedule}
                    canEditCursors={canEditCursors}
                    importer={importer}
                    isRecurrable={isRecurrable}
                    destination={destination}
                  />
                ),
              },
            ]}
          />
        </Section>
      </Container>
    </>
  );
};

export default DashboardImporterView;
